
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import { FsAcl } from '@firestitch/acl';
import { FsBadgeModule } from '@firestitch/badge';
import { FilterConfig, FsFilterModule, ItemType } from '@firestitch/filter';
import { FsSidenavModule, MENU_DATA, SidenavMenuRef } from '@firestitch/sidenav';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectData } from '@common/data';
import { AclPermission } from '@common/enums';
import { Project } from '@common/interfaces';
import { SessionService } from '@common/services';

import { SidenavService } from '@libs/layouts/side-nav/services';
import { ProjectCreateComponent } from '@libs/project-create/components';


@Component({
  templateUrl: './search-project.component.html',
  styleUrls: ['./search-project.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    MatButtonModule,
    FsSidenavModule,
    FsFilterModule,
    FsBadgeModule,
  ],
})
export class SearchProjectComponent implements OnInit, OnDestroy {
  
  public menuRef = inject<SidenavMenuRef<SearchProjectComponent>>(SidenavMenuRef);
  public data = inject(MENU_DATA);
  public projects: Project[] = [];
  public allowCreate = false;
  public config: FilterConfig;
  public hasDocPermissions = false;

  private _destroy$ = new Subject<void>();
  private _sessionService = inject(SessionService);
  private _acl = inject(FsAcl);
  private _projectData = inject(ProjectData);
  private _sidenavService = inject(SidenavService);
  private _cdRef = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);

  public ngOnInit(): void {
    this.hasDocPermissions = this._acl
      .hasRead(AclPermission.Doc, [this._sessionService.environmentId()]);
    this.allowCreate = this._acl
      .hasWrite(AclPermission.Environment, this._sessionService.environmentId()) ||
      this._acl.hasWrite(AclPermission.Project, this._sessionService.environmentId());

    this.config = {
      queryParam: false,
      autofocus: true,
      items: [
        {
          name: 'keyword',
          type: ItemType.Keyword,
          label: 'Search',
        },
      ],
      change: (query) => {
        this._load(query);
      },
      init: (query) => {
        this._load(query);
      },
    };
  }

  public close(): void {
    this.menuRef.close();
    this._sidenavService.hideMobile();
  }

  public create(): void {
    this.close();

    const dialogRef = this._dialog.open(ProjectCreateComponent, {
      data: {},
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((response) => {
        if (response) {
          this._router.navigate(['/projects', response.id]);
        }
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _load(query: any): void {
    query.environmentId = this._sessionService.environmentId();
    query.recent = 'desc';

    this._projectData.gets(query)
      .subscribe((response) => {
        this.projects = response;
        this._cdRef.markForCheck();
      });
  }

}
